import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatTRY, TR_MONTHS } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { ChevronDown, Fuel as FuelIcon, CreditCard, Landmark, Loader2 } from "lucide-react";

const TYPE_META = {
  fuel: { label: "Yakıt", color: "#F59E0B", icon: FuelIcon },
  card: { label: "Kart", color: "#22C55E", icon: CreditCard },
  loan: { label: "Kredi", color: "#8B5CF6", icon: Landmark },
};

export default function MonthlyPlan() {
  const [months, setMonths] = useState([]);
  const [range, setRange] = useState(6);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async (r = range) => {
    setLoading(true);
    try {
      const res = await api.get(`/plan/monthly?months=${r}`);
      setMonths(res.data.months || []);
    } catch (e) {
      setMonths([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const grandTotal = months.reduce((s, m) => s + m.total, 0);

  return (
    <div className="p-5 md:p-8 max-w-5xl" data-testid="monthly-plan-page">
      <PageHeader
        title="Aylık Ödeme Planı"
        subtitle="Yakıt, kredi kartı ve kredi ödemelerinizin aylara göre toplam dökümü. Bir aya tıklayarak detayları görün."
        testid="monthly-header"
        action={
          <select
            data-testid="monthly-range-select"
            value={range}
            onChange={(e) => {
              const v = Number(e.target.value);
              setRange(v);
              load(v);
            }}
            className="bg-[#111620] border border-[#253043] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#E65100]"
          >
            <option value={3}>3 Ay</option>
            <option value={6}>6 Ay</option>
            <option value={12}>12 Ay</option>
          </select>
        }
      />

      <div className="card-surface p-4 mb-5 flex items-center justify-between fade-up">
        <span className="text-sm text-[#94A3B8]">Seçili dönem toplam ödeme</span>
        <span className="font-mono-num font-extrabold text-xl text-[#E65100]" data-testid="monthly-grand-total">
          {formatTRY(grandTotal)}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#E65100]" />
        </div>
      ) : months.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#94A3B8] text-sm">
          Bu dönemde planlanmış ödeme bulunamadı. Aytemiz verisi ekleyin veya kredi/kart tanımlayın.
        </div>
      ) : (
        <div className="space-y-3">
          {months.map((m) => {
            const isOpen = open === m.key;
            return (
              <div key={m.key} className="card-surface overflow-hidden fade-up" data-testid="monthly-row">
                <button
                  onClick={() => setOpen(isOpen ? null : m.key)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-[#161D2A] transition-colors text-left"
                  data-testid="monthly-row-toggle"
                >
                  <div className="w-12 text-center">
                    <div className="font-display font-extrabold text-lg leading-none">{TR_MONTHS[m.month - 1].slice(0, 3)}</div>
                    <div className="text-[11px] text-[#94A3B8]">{m.year}</div>
                  </div>
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <MiniStat label="Yakıt" value={m.fuel} color="#F59E0B" />
                    <MiniStat label="Kartlar" value={m.card} color="#22C55E" />
                    <MiniStat label="Krediler" value={m.loan} color="#8B5CF6" />
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-[#94A3B8]">Toplam</div>
                    <div className="font-mono-num font-bold">{formatTRY(m.total)}</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-[#253043] divide-y divide-[#1B2430]">
                    {m.items.map((it, i) => {
                      const meta = TYPE_META[it.type];
                      const Icon = meta.icon;
                      return (
                        <div key={i} className="flex items-center gap-3 px-4 py-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}22` }}>
                            <Icon className="w-4 h-4" style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{it.title}</div>
                            <div className="text-[11px] text-[#94A3B8]">{it.date.split("-").reverse().join(".")}</div>
                          </div>
                          <div className="font-mono-num text-sm font-medium">{formatTRY(it.amount)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] text-[#94A3B8]">{label}</div>
      <div className="font-mono-num text-sm font-semibold" style={{ color: value > 0 ? color : "#475569" }}>
        {formatTRY(value)}
      </div>
    </div>
  );
}
